"use client";

import { Handle, type NodeProps, Position } from "@xyflow/react";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { memo, useState } from "react";
import { useDirection } from "../DirectionContext";
import type { AICheckNodeData, CheckInfo } from "../types";
import { AdminNodeActions } from "./shared/AdminNodeActions";
import { ElapsedTime } from "./shared/ElapsedTime";
import { NodeFrame } from "./shared/NodeFrame";
import { ProgressBar } from "./shared/ProgressBar";
import {
  calcBarPercent,
  formatProgressMetrics,
} from "./shared/progress-metrics";
import { StatusBadge } from "./shared/StatusBadge";
import { STATUS_COLORS } from "./shared/status-styles";

const BATCH_VISIBLE_DEFAULT = 2;

function CheckRow({ check }: { check: CheckInfo }) {
  return (
    <div className="nodrag flex items-center gap-1.5 text-[10px]">
      <StatusBadge status={check.status} />
      <span className="cursor-text select-text text-gray-600 whitespace-nowrap">
        {check.name}
      </span>
      {check.attempt && check.attempt >= 2 && (
        <span className="ml-auto text-amber-500 shrink-0">
          retry #{check.attempt}
        </span>
      )}
    </div>
  );
}

export const AICheckNode = memo(function AICheckNode({ id, data }: NodeProps) {
  const d = data as AICheckNodeData;
  const dir = useDirection();
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const checks = d.checks ?? [];
  const barPct = d.contentLength ? calcBarPercent(d.contentLength) : 0;
  const errorChecks = checks.filter((c) => c.error);
  const groupedErrors = new Map<string, CheckInfo[]>();
  for (const check of errorChecks) {
    const key = check.error ?? "Unknown error";
    const existing = groupedErrors.get(key);
    if (existing) {
      existing.push(check);
    } else {
      groupedErrors.set(key, [check]);
    }
  }
  const hasError = errorChecks.length > 0;
  const colors = STATUS_COLORS[d.status];
  const isBatch = d.batchSize > 1;
  const isFailed = d.status === "failed";

  const hasMore = isBatch && checks.length > BATCH_VISIBLE_DEFAULT;
  const visibleChecks =
    isBatch && !expanded ? checks.slice(0, BATCH_VISIBLE_DEFAULT) : checks;
  const hiddenCount = checks.length - BATCH_VISIBLE_DEFAULT;
  const isActive = d.status === "active";
  const isReceiving =
    (d.tokensReceived ?? 0) > 0 ||
    (d.bytesReceived ?? 0) > 0 ||
    (d.contentLength ?? 0) > 0;

  let stateClass = "";
  if (d.status === "pending") {
    stateClass = "!bg-amber-50 !border-l-amber-200";
  } else if (isActive && isReceiving) {
    stateClass =
      "pipeline-node-active-streaming !bg-blue-50 !border-l-blue-400";
  } else if (isActive) {
    stateClass =
      "pipeline-node-active-waiting !bg-amber-100 !border-l-amber-400";
  }

  return (
    <NodeFrame
      status={d.status}
      className={`min-w-[200px] w-max max-w-[360px] ${stateClass}`}
    >
      <Handle
        type="target"
        position={dir === "LR" ? Position.Left : Position.Top}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <StatusBadge status={d.status} />
          <span
            className={`nodrag cursor-text select-text text-xs font-medium ${colors.text} whitespace-nowrap`}
          >
            {d.label}
          </span>
          {isBatch && (
            <span className="text-[10px] text-gray-400 bg-gray-100 rounded px-1 py-0.5 shrink-0">
              {d.batchSize}
            </span>
          )}
        </div>
        <ElapsedTime ms={d.elapsedMs} />
      </div>
      {visibleChecks.length > 0 && (
        <div className="border-t border-gray-100 pt-1 mt-1 flex flex-col gap-0.5">
          {visibleChecks.map((c) => (
            <CheckRow key={c.name} check={c} />
          ))}
        </div>
      )}
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-0.5 flex items-center gap-0.5 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
        >
          <ChevronDown
            size={10}
            className={`transition-transform ${expanded ? "rotate-180" : ""}`}
          />
          {expanded ? "collapse" : `+${hiddenCount} more`}
        </button>
      )}
      <div className="mt-1.5">
        <ProgressBar percent={barPct} status={d.status} />
        <div
          className={`nodrag cursor-text select-text text-[10px] mt-0.5 tabular-nums ${d.status === "completed" ? "text-emerald-500" : "text-gray-400"}`}
        >
          {formatProgressMetrics(
            d.tokensReceived,
            d.contentLength,
            d.bytesReceived,
          )}
        </div>
      </div>
      <AdminNodeActions
        nodeId={d.nodeId ?? id}
        nodeStatus={d.status}
        nodeExecutionDirectives={d.nodeExecutionDirectives}
        onAdminAction={d.onAdminAction}
      />
      {isFailed && (
        <div className="mt-1 flex items-center gap-1.5">
          <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
            Auto-retried once before failing
          </span>
          {hasError && (
            <button
              type="button"
              onClick={() => setShowErrorDialog(true)}
              className="text-[10px] px-1.5 py-0.5 rounded border border-red-300 text-red-600 hover:bg-red-50 transition-colors inline-flex items-center gap-1"
            >
              <AlertTriangle size={10} />
              Details
            </button>
          )}
        </div>
      )}
      {showErrorDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="nodrag bg-white rounded-lg shadow-xl border border-red-200 w-[min(520px,90vw)] max-h-[70vh] overflow-auto">
            <div className="px-4 py-2.5 border-b border-red-100 flex items-center justify-between">
              <div className="nodrag cursor-text select-text text-sm font-semibold text-red-700">
                AI Check Error Details
              </div>
              <button
                type="button"
                onClick={() => setShowErrorDialog(false)}
                className="nodrag text-xs text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>
            <div className="p-3 space-y-2">
              <div className="nodrag cursor-text select-text text-xs font-semibold text-red-700">
                Batch: {d.label}
              </div>
              {Array.from(groupedErrors.entries()).map(([error, grouped]) => (
                <div
                  key={`${d.label}-${error}`}
                  className="rounded border border-red-200 bg-red-50 p-2"
                >
                  <div className="nodrag cursor-text select-text text-xs font-medium text-red-700">
                    Affected checks ({grouped.length})
                  </div>
                  <div className="nodrag cursor-text select-text mt-1 text-xs text-red-600">
                    {grouped
                      .map(
                        (c) => `${c.name}${c.checkId ? ` (${c.checkId})` : ""}`,
                      )
                      .join(", ")}
                  </div>
                  <div className="nodrag cursor-text select-text mt-1 text-xs text-red-700 whitespace-pre-wrap break-words">
                    {error}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <Handle
        type="source"
        position={dir === "LR" ? Position.Right : Position.Bottom}
        className="!bg-transparent !border-0 !w-0 !h-0"
      />
    </NodeFrame>
  );
});
